import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  shapes,
  themes,
} from '@backstage/theme';

const colors = {
  background: '#0b1020',
  surface: '#11182a',
  surfaceRaised: '#17213a',
  surfaceHover: '#1e2a47',
  border: '#34415f',
  borderSubtle: '#26324d',
  text: '#f6f9fc',
  textMuted: '#adbdcc',
  link: '#80e9ff',
  blue: '#7a73ff',
  blueDark: '#635bff',
  green: '#3ecf8e',
  orange: '#ffb020',
  red: '#ff6b81',
};

const pageTheme = Object.fromEntries(
  [
    'home',
    'documentation',
    'tool',
    'service',
    'website',
    'library',
    'other',
    'app',
    'apis',
    'card',
  ].map(name => [
    name,
    genPageTheme({
      colors: [colors.blueDark, '#00b8d9'],
      shape: shapes.none,
    }),
  ]),
);

const palette = {
  ...themes.dark.getTheme('v5')?.palette,
  mode: 'dark' as const,
  background: {
    default: colors.background,
    paper: colors.surface,
  },
  primary: {
    main: colors.blue,
    light: colors.link,
    dark: colors.blueDark,
    contrastText: '#ffffff',
  },
  secondary: {
    main: colors.link,
    light: '#b8f4ff',
    dark: '#00a4bd',
    contrastText: '#07111f',
  },
  error: {
    main: colors.red,
    light: '#ff9aaa',
    dark: '#d94a61',
  },
  warning: {
    main: colors.orange,
    light: '#ffd27a',
    dark: '#d98c00',
  },
  info: {
    main: colors.blue,
    light: colors.link,
    dark: colors.blueDark,
  },
  success: {
    main: colors.green,
    light: '#74e8b7',
    dark: '#22a96f',
  },
  text: {
    primary: colors.text,
    secondary: colors.textMuted,
    disabled: '#65738f',
  },
  divider: colors.borderSubtle,
  navigation: {
    background: '#0d1425',
    indicator: colors.blue,
    color: colors.textMuted,
    selectedColor: colors.text,
    navItem: {
      hoverBackground: colors.surfaceHover,
    },
    submenu: {
      background: colors.surface,
    },
  },
};

const typography = {
  htmlFontSize: 16,
  fontFamily:
    '"RedHatText", "Red Hat Text", "Helvetica Neue", Arial, sans-serif',
  h1: { fontSize: 32, fontWeight: 600, marginBottom: 12 },
  h2: { fontSize: 28, fontWeight: 600, marginBottom: 10 },
  h3: { fontSize: 24, fontWeight: 600, marginBottom: 8 },
  h4: { fontSize: 20, fontWeight: 600, marginBottom: 6 },
  h5: { fontSize: 17, fontWeight: 600, marginBottom: 4 },
  h6: { fontSize: 15, fontWeight: 600, marginBottom: 2 },
  body1: { fontSize: 14 },
  body2: { fontSize: 13 },
  button: { fontWeight: 600, textTransform: 'none' as const },
};

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: colors.background,
        backgroundImage:
          'radial-gradient(circle at 12% 0%, rgba(99,91,255,0.18), transparent 32%), radial-gradient(circle at 88% 8%, rgba(0,212,255,0.10), transparent 28%)',
        backgroundAttachment: 'fixed',
        color: colors.text,
      },
      a: {
        color: colors.link,
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      header: {
        minHeight: 'auto',
        height: 'auto',
        padding: '28px 36px 20px',
        backgroundColor: colors.surface,
        backgroundImage:
          'linear-gradient(120deg, rgba(99,91,255,0.20), rgba(0,212,255,0.07) 48%, rgba(17,24,42,0.96))',
        borderBottom: `1px solid ${colors.border}`,
        boxShadow: '0 12px 36px rgba(2,6,23,0.22)',
      },
      title: {
        color: colors.text,
        fontSize: '2rem',
        fontWeight: 600,
      },
      subtitle: {
        color: colors.textMuted,
      },
      leftItemsBox: {
        flex: 1,
      },
      rightItemsBox: {
        marginLeft: 'auto',
      },
    },
  },
  BackstageContent: {
    styleOverrides: {
      root: {
        padding: '28px 36px 40px',
        backgroundColor: 'transparent',
      },
    },
  },
  BackstageInfoCard: {
    styleOverrides: {
      root: {
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
        boxShadow: '0 16px 38px rgba(2,6,23,0.24)',
      },
      header: {
        minHeight: 52,
        backgroundColor: colors.surfaceRaised,
        backgroundImage:
          'linear-gradient(120deg, rgba(122,115,255,0.10), rgba(0,212,255,0.03))',
        borderBottom: `1px solid ${colors.border}`,
      },
      headerTitle: {
        color: colors.text,
        fontWeight: 600,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: colors.surface,
        borderColor: colors.border,
        boxShadow: '0 12px 30px rgba(2,6,23,0.20)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
        backgroundImage:
          'linear-gradient(145deg, rgba(255,255,255,0.035), rgba(255,255,255,0))',
        boxShadow: '0 16px 38px rgba(2,6,23,0.24)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        minHeight: 36,
        borderRadius: 8,
        textTransform: 'none' as const,
        fontWeight: 600,
      },
      containedPrimary: {
        color: '#ffffff',
        backgroundColor: colors.blueDark,
        '&:hover': {
          backgroundColor: colors.blue,
        },
      },
      outlined: {
        borderColor: colors.border,
        '&:hover': {
          borderColor: colors.link,
          backgroundColor: colors.surfaceHover,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: colors.text,
        borderRadius: 8,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        color: colors.text,
        backgroundColor: colors.surfaceRaised,
      },
    },
  },
  // The input root is positioned and comes after its label, so its background
  // hides an unshrunk label unless the label stacks above it.
  MuiInputLabel: {
    styleOverrides: {
      root: {
        zIndex: 1,
        pointerEvents: 'none',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: colors.border,
      },
      root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.link,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.blue,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        color: colors.text,
        borderBottom: `1px solid ${colors.border}`,
      },
      head: {
        color: colors.text,
        fontWeight: 600,
        backgroundColor: colors.surfaceRaised,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: `${colors.surfaceHover} !important`,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        color: colors.text,
        backgroundColor: colors.surfaceRaised,
        borderRadius: 999,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: colors.blue,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        color: colors.textMuted,
        textTransform: 'none' as const,
        '&.Mui-selected': {
          color: colors.text,
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.surface,
        borderLeft: `1px solid ${colors.border}`,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 14,
      },
    },
  },
};

export const openshiftConsoleTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette,
    pageTheme,
    typography,
    defaultPageTheme: 'home',
  }),
  components,
});
